import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { publicReviewApi } from "../services/api";

// ─── Types ──────────────────────────────────────────────────────────────────

interface FirmInfo {
  firmName: string;
  logoUrl?: string;
  primaryColor?: string;
  googleReviewUrl?: string;
  customThankYouMessage?: string;
  customFeedbackPrompt?: string;
}

type Step = "rating" | "redirect" | "feedback" | "thankyou" | "error" | "loading" | "already";

// ─── Star SVG ───────────────────────────────────────────────────────────────

const StarIcon: React.FC<{
  filled: boolean;
  hovered: boolean;
  size?: number;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}> = ({ filled, hovered, size = 48, onClick, onMouseEnter, onMouseLeave }) => (
  <motion.svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    className="cursor-pointer select-none"
    whileHover={{ scale: 1.2 }}
    whileTap={{ scale: 0.9 }}
    transition={{ type: "spring", stiffness: 400, damping: 15 }}
  >
    <defs>
      <linearGradient id="starGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FBBF24" />
        <stop offset="100%" stopColor="#F59E0B" />
      </linearGradient>
    </defs>
    <path
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      fill={filled || hovered ? "url(#starGold)" : "none"}
      stroke={filled || hovered ? "#F59E0B" : "#D1D5DB"}
      strokeWidth={1.5}
      strokeLinejoin="round"
      strokeLinecap="round"
    />
    {filled && (
      <motion.path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
        fill="url(#starGold)"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      />
    )}
  </motion.svg>
);

// ─── Sparkle Particle ───────────────────────────────────────────────────────

const Sparkle: React.FC<{ delay: number; x: number; y: number }> = ({ delay, x, y }) => (
  <motion.div
    className="absolute w-2 h-2 rounded-full"
    style={{
      background: "linear-gradient(135deg, #FBBF24, #F59E0B)",
      left: "50%",
      top: "50%",
    }}
    initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
    animate={{
      opacity: [1, 1, 0],
      x: x,
      y: y,
      scale: [0.5, 1.2, 0],
    }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  />
);

// ─── Confetti Burst ─────────────────────────────────────────────────────────

const ConfettiBurst: React.FC = () => {
  const particles = Array.from({ length: 16 }, (_, i) => {
    const angle = (i / 16) * Math.PI * 2;
    const distance = 60 + Math.random() * 40;
    return {
      id: i,
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
      delay: Math.random() * 0.15,
    };
  });

  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
      {particles.map((p) => (
        <Sparkle key={p.id} delay={p.delay} x={p.x} y={p.y} />
      ))}
    </div>
  );
};

// ─── Step Wrapper ───────────────────────────────────────────────────────────

const StepWrapper: React.FC<{ children: React.ReactNode; stepKey: string }> = ({ children, stepKey }) => (
  <motion.div
    key={stepKey}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
    className="w-full"
  >
    {children}
  </motion.div>
);

// ─── Main Component ─────────────────────────────────────────────────────────

const ReviewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>("loading");
  const [firmInfo, setFirmInfo] = useState<FirmInfo>({ firmName: "" });
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (!token) {
      setStep("error");
      return;
    }
    publicReviewApi
      .get(token)
      .then((res) => {
        const data = res.data;
        if (data.alreadySubmitted) {
          setStep("already");
        } else {
          setFirmInfo({
            firmName: data.firmName || "Our Firm",
            logoUrl: data.logoUrl,
            primaryColor: data.primaryColor,
            googleReviewUrl: data.googleReviewUrl,
            customThankYouMessage: data.customThankYouMessage,
            customFeedbackPrompt: data.customFeedbackPrompt,
          });
          setStep("rating");
        }
      })
      .catch(() => {
        setStep("error");
      });
  }, [token]);

  const handleStarClick = useCallback(
    (star: number) => {
      setRating(star);
      if (star === 5) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 1200);
        setTimeout(() => setStep("redirect"), 800);
      } else {
        setTimeout(() => setStep("feedback"), 400);
      }
    },
    []
  );

  const handleSubmitFeedback = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      await publicReviewApi.submit(token, {
        rating,
        feedback,
        name: name || undefined,
        email: email || undefined,
      });
      setStep("thankyou");
    } catch {
      // Still show thank you on error — feedback is best-effort
      setStep("thankyou");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleRedirect = () => {
    if (!token) return;
    // Fire-and-forget: record that the 5-star user was sent to Google
    publicReviewApi.submit(token, { rating: 5, redirectedToGoogle: true }).catch(() => {});
    if (firmInfo.googleReviewUrl) {
      window.open(firmInfo.googleReviewUrl, "_blank", "noopener");
    }
    setStep("thankyou");
  };

  const handleSkipGoogle = () => {
    if (!token) return;
    publicReviewApi.submit(token, { rating: 5, redirectedToGoogle: false }).catch(() => {});
    setStep("thankyou");
  };

  const primaryColor = firmInfo.primaryColor || "#0F5132";

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 px-4">
      <div className="w-full max-w-lg">
        <AnimatePresence mode="wait">
          {/* Loading */}
          {step === "loading" && (
            <StepWrapper stepKey="loading">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10 text-center">
                <div
                  className="animate-spin h-8 w-8 rounded-full border-4 mx-auto"
                  style={{ borderColor: primaryColor, borderTopColor: "transparent" }}
                />
                <p className="mt-4 text-sm text-slate-500">Loading...</p>
              </div>
            </StepWrapper>
          )}

          {/* Error */}
          {step === "error" && (
            <StepWrapper stepKey="error">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10 text-center">
                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Link Not Found</h2>
                <p className="text-slate-500">This review link is invalid or has expired.</p>
              </div>
            </StepWrapper>
          )}

          {/* Already submitted */}
          {step === "already" && (
            <StepWrapper stepKey="already">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Already Submitted</h2>
                <p className="text-slate-500">You have already submitted your feedback. Thank you!</p>
              </div>
            </StepWrapper>
          )}

          {/* Step 1: Rating */}
          {step === "rating" && (
            <StepWrapper stepKey="rating">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10 text-center relative overflow-hidden">
                {/* Logo or Firm Name */}
                {firmInfo.logoUrl ? (
                  <img
                    src={firmInfo.logoUrl}
                    alt={firmInfo.firmName}
                    className="h-12 mx-auto mb-6 object-contain"
                  />
                ) : (
                  <h1
                    className="text-2xl font-bold mb-2"
                    style={{ color: primaryColor }}
                  >
                    {firmInfo.firmName}
                  </h1>
                )}

                <p className="text-slate-600 mb-1 text-lg">
                  How was your experience with
                </p>
                <p className="text-slate-800 font-semibold text-lg mb-8">
                  {firmInfo.firmName}?
                </p>

                {/* Stars */}
                <div className="flex items-center justify-center gap-3 mb-6 relative">
                  {showConfetti && <ConfettiBurst />}
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      filled={star <= rating}
                      hovered={star <= hoveredStar && rating === 0}
                      size={52}
                      onClick={() => handleStarClick(star)}
                      onMouseEnter={() => setHoveredStar(star)}
                      onMouseLeave={() => setHoveredStar(0)}
                    />
                  ))}
                </div>

                <p className="text-sm text-slate-400">
                  {rating === 0 ? "Tap a star to rate" : ""}
                </p>
              </div>
            </StepWrapper>
          )}

          {/* Step 2a: 5-Star Redirect */}
          {step === "redirect" && (
            <StepWrapper stepKey="redirect">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.1 }}
                  className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-6"
                >
                  <span className="text-3xl">&#127881;</span>
                </motion.div>

                <h2 className="text-2xl font-bold text-slate-800 mb-3">Thank you!</h2>

                <p className="text-slate-600 mb-2">
                  {firmInfo.customThankYouMessage ||
                    "We're thrilled you had a great experience."}
                </p>
                {firmInfo.googleReviewUrl && (
                  <p className="text-slate-500 mb-8 text-sm">
                    Would you mind sharing your feedback on Google? It really helps us.
                  </p>
                )}

                <div className="flex flex-col gap-3">
                  {firmInfo.googleReviewUrl && (
                    <button
                      onClick={handleGoogleRedirect}
                      className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl text-white font-bold text-sm transition-colors"
                      style={{ backgroundColor: primaryColor }}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = primaryColor)}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                      Leave a Google Review
                    </button>
                  )}
                  <button
                    onClick={handleSkipGoogle}
                    className="w-full py-3 px-6 rounded-xl text-slate-500 font-medium text-sm hover:bg-slate-50 transition-colors border border-slate-200"
                  >
                    No thanks, I'm done
                  </button>
                </div>
              </div>
            </StepWrapper>
          )}

          {/* Step 2b: Feedback Form (1-4 stars) */}
          {step === "feedback" && (
            <StepWrapper stepKey="feedback">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10">
                {/* Show selected stars */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      filled={star <= rating}
                      hovered={false}
                      size={28}
                      onClick={() => {}}
                      onMouseEnter={() => {}}
                      onMouseLeave={() => {}}
                    />
                  ))}
                </div>

                <h2 className="text-xl font-bold text-slate-800 mb-2 text-center">
                  Thank you for your feedback.
                </h2>
                <p className="text-slate-500 text-center mb-6">
                  {firmInfo.customFeedbackPrompt ||
                    "We're sorry your experience wasn't perfect. Your feedback helps us improve."}
                </p>

                <div className="space-y-4">
                  <div>
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Tell us how we can do better..."
                      rows={4}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">
                      Name <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">
                      Email <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                    />
                  </div>

                  <button
                    onClick={handleSubmitFeedback}
                    disabled={submitting}
                    className="w-full py-3.5 px-6 rounded-xl text-white font-bold text-sm transition-colors disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                    onMouseOver={(e) => {
                      if (!submitting) e.currentTarget.style.backgroundColor = "#146C43";
                    }}
                    onMouseOut={(e) => {
                      if (!submitting) e.currentTarget.style.backgroundColor = primaryColor;
                    }}
                  >
                    {submitting ? "Submitting..." : "Submit Feedback"}
                  </button>
                </div>
              </div>
            </StepWrapper>
          )}

          {/* Step 3: Thank You */}
          {step === "thankyou" && (
            <StepWrapper stepKey="thankyou">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6"
                >
                  <svg
                    className="w-8 h-8 text-emerald-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <motion.path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.4, delay: 0.2 }}
                    />
                  </svg>
                </motion.div>

                <h2 className="text-2xl font-bold text-slate-800 mb-3">Thank you!</h2>
                <p className="text-slate-500">
                  Your feedback has been received.<br />
                  We appreciate you taking the time.
                </p>
              </div>
            </StepWrapper>
          )}
        </AnimatePresence>
      </div>

      {/* Powered by Kavoa footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-slate-400">
          Powered by{" "}
          <span className="font-semibold" style={{ color: "#0F5132" }}>
            Kavoa
          </span>
        </p>
      </div>
    </div>
  );
};

export default ReviewPage;
